import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signUp, signInWithGoogle } from '@/lib/authService';
import { useAuthStore } from '@/stores/authStore';
import { fetchWithAuth } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GachaAnimation } from '@/components/GachaAnimation';
import toast from 'react-hot-toast';
import { ScpLogo } from '@/components/ScpLogo';

export function SignUpPage() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [welcomeCards, setWelcomeCards] = useState<Array<{
    cardId: string; scpCode: string; name: string;
    rarity: 'common' | 'rare' | 'epic' | 'legendary';
    imageUrl: string | null; isDuplicate: boolean;
    fragments: number; stats: Record<string, number>;
  }> | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
  });
  const [agreed, setAgreed] = useState(false);

  const handleLogin = async (user: { uid: string; email: string | null; displayName: string | null }, role: string) => {
    login(
      user.uid,
      user.email || '',
      user.displayName || '',
      role as 'user' | 'aspirant' | 'author' | 'admin' | 'super-admin'
    );

    if (role === 'super-admin') {
      toast.success('관리자 회원가입 성공! 관리자 페이지로 이동합니다.');
      navigate('/admin');
      return;
    }

    // 웰컴 가챠 호출
    try {
      const res = await fetchWithAuth('/api/gacha/welcome', { method: 'POST' });
      const json = await res.json();
      if (json.success && json.data.cards.length > 0) {
        setWelcomeCards(json.data.cards.map((c: { cardId: string; scpCode: string; name: string; rarity: 'common' | 'rare' | 'epic' | 'legendary'; imageUrl: string | null; stats: Record<string, number> }) => ({
          cardId: c.cardId,
          scpCode: c.scpCode,
          name: c.name,
          rarity: c.rarity,
          imageUrl: c.imageUrl,
          isDuplicate: false,
          fragments: 0,
          stats: c.stats,
        })));
        return; // 애니메이션 끝나면 이동
      }
    } catch {
      // 웰컴 가챠 실패해도 가입은 성공
    }

    toast.success('회원가입 성공!');
    navigate('/');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('비밀번호가 일치하지 않습니다');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('비밀번호는 6자 이상이어야 합니다');
      return;
    }

    if (!agreed) {
      toast.error('이용약관·개인정보처리방침·만 14세 이상에 동의해주세요.');
      return;
    }

    setIsLoading(true);

    try {
      const { user, role, error } = await signUp({
        email: formData.email,
        password: formData.password,
        username: formData.username,
      });

      if (error) {
        toast.error(error);
      } else if (user) {
        await handleLogin(user, role);
      }
    } catch (err) {
      toast.error('회원가입 중 오류가 발생했습니다');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    if (!agreed) {
      toast.error('이용약관·개인정보처리방침·만 14세 이상에 동의해주세요.');
      return;
    }
    setIsGoogleLoading(true);

    try {
      const { user, role, error } = await signInWithGoogle();

      if (error) {
        toast.error(error);
      } else if (user) {
        await handleLogin(user, role);
      }
    } catch (err) {
      toast.error('Google 가입 중 오류가 발생했습니다');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="container flex items-center justify-center min-h-[calc(100vh-4rem)] py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-3 items-center text-center">
          <ScpLogo size={48} />
          <CardTitle className="text-2xl font-bold">회원가입</CardTitle>
          <CardDescription>
            SCP Novel에서 창작 웹소설을 즐겨보세요
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Google 가입 */}
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={isGoogleLoading}
            onClick={handleGoogleSignUp}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            {isGoogleLoading ? 'Google 가입 중...' : 'Google로 시작하기'}
          </Button>

          {/* 구분선 */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">또는</span>
            </div>
          </div>

          {/* 이메일 가입 */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">사용자명</Label>
              <Input
                id="username"
                type="text"
                placeholder="홍길동"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">이메일</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">비밀번호</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">비밀번호 확인</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              variant="scp"
              disabled={isLoading}
            >
              {isLoading ? '가입 중...' : '이메일로 회원가입'}
            </Button>
          </form>

          {/* 약관 동의 */}
          <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none rounded-md p-2 border bg-muted/30">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 shrink-0"
            />
            <span>
              <Link to="/terms" target="_blank" className="text-primary hover:underline">이용약관</Link>,{' '}
              <Link to="/privacy" target="_blank" className="text-primary hover:underline">개인정보처리방침</Link>에 동의하며,
              <strong className="text-foreground"> 만 14세 이상</strong>임을 확인합니다.
            </span>
          </label>

          <div className="text-center text-sm">
            <span className="text-muted-foreground">이미 계정이 있으신가요? </span>
            <Link to="/login" className="text-primary hover:underline">
              로그인
            </Link>
          </div>
        </CardContent>
      </Card>
      {welcomeCards && (
        <GachaAnimation
          cards={welcomeCards}
          title="SCP 재단 신입 연구원 지급품"
          onComplete={() => {
            setWelcomeCards(null);
            toast.success('카드 6장이 지급되었습니다! 배틀을 시작해보세요.');
            navigate('/arcade');
          }}
        />
      )}
    </div>
  );
}
